import React, { useState } from 'react';
import { PageHeader } from '../components/dashboard/PageHeader';
import { Card, CardContent } from '../components/Card';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Building2, CheckCircle2, Clock, AlertTriangle, ChevronRight, Network, Users, Gauge, ListTodo, ArrowRight } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/Sheet';
import { toast } from 'sonner';
type Stakeholder = {
  code: string;
  name: string;
  role: 'utama' | 'pendukung' | 'koordinasi';
  responded: boolean;
  responseTime?: string;
};
type Action = {
  horizon: '24j' | '72j' | '7h';
  title: string;
  pic: string;
  status: 'done' | 'progress' | 'pending';
};
type Issue = {
  id: string;
  title: string;
  category: string;
  severity: 'KRITIS' | 'TINGGI' | 'SEDANG';
  region: string;
  responsivenessScore: number;
  gap: string;
  stakeholders: Stakeholder[];
  actions: Action[];
  insight: string;
};
const issues: Issue[] = [{
  id: 'M-001',
  title: 'Harga beras melambung 18%',
  category: 'EKONOMI',
  severity: 'KRITIS',
  region: 'Jawa, Sumatera',
  responsivenessScore: 42,
  gap: 'Bulog belum merilis data stok terbaru. Pemda belum operasi pasar.',
  stakeholders: [{
    code: 'KEMTAN',
    name: 'Kementerian Pertanian',
    role: 'utama',
    responded: true,
    responseTime: '4 jam'
  }, {
    code: 'BAPANAS',
    name: 'Badan Pangan Nasional',
    role: 'utama',
    responded: true,
    responseTime: '2 jam'
  }, {
    code: 'BULOG',
    name: 'Perum Bulog',
    role: 'utama',
    responded: false
  }, {
    code: 'KEMDAG',
    name: 'Kementerian Perdagangan',
    role: 'pendukung',
    responded: true,
    responseTime: '6 jam'
  }, {
    code: 'KEMENDAGRI',
    name: 'Kementerian Dalam Negeri',
    role: 'koordinasi',
    responded: false
  }, {
    code: 'PEMDA',
    name: 'Pemerintah Daerah (8 prov)',
    role: 'pendukung',
    responded: false
  }, {
    code: 'POLRI',
    name: 'Satgas Pangan POLRI',
    role: 'koordinasi',
    responded: true,
    responseTime: '3 jam'
  }],
  actions: [{
    horizon: '24j',
    title: 'Rilis data stok beras nasional resmi',
    pic: 'BULOG',
    status: 'pending'
  }, {
    horizon: '24j',
    title: 'Operasi pasar di 8 provinsi prioritas',
    pic: 'KEMDAG + PEMDA',
    status: 'progress'
  }, {
    horizon: '72j',
    title: 'Distribusi tambahan ke daerah defisit',
    pic: 'BULOG + BAPANAS',
    status: 'pending'
  }, {
    horizon: '72j',
    title: 'Konferensi pers terkoordinasi',
    pic: 'KEMTAN',
    status: 'done'
  }, {
    horizon: '7h',
    title: 'Evaluasi pola distribusi & importasi darurat',
    pic: 'BAPANAS',
    status: 'pending'
  }],
  insight: 'Respons KEMTAN dan BAPANAS sudah ada tetapi tidak terkoordinasi dengan BULOG. Risiko naik 24 jam ke depan jika Bulog tidak merilis data stok.'
}, {
  id: 'M-002',
  title: 'Hoaks bansos pangan dihentikan',
  category: 'SOSIAL',
  severity: 'TINGGI',
  region: 'Nasional',
  responsivenessScore: 68,
  gap: 'Klarifikasi sudah ada di Komdigi, tapi belum disebar ke daerah secara konsisten.',
  stakeholders: [{
    code: 'KEMENSOS',
    name: 'Kementerian Sosial',
    role: 'utama',
    responded: true,
    responseTime: '1 jam'
  }, {
    code: 'KOMDIGI',
    name: 'Komdigi',
    role: 'utama',
    responded: true,
    responseTime: '45 menit'
  }, {
    code: 'KSP',
    name: 'Kantor Staf Presiden',
    role: 'pendukung',
    responded: true,
    responseTime: '2 jam'
  }, {
    code: 'PEMDA',
    name: 'Pemerintah Daerah',
    role: 'pendukung',
    responded: false
  }],
  actions: [{
    horizon: '24j',
    title: 'Sebar klarifikasi resmi ke seluruh pemda',
    pic: 'KSP',
    status: 'progress'
  }, {
    horizon: '24j',
    title: 'Produksi infografis & video pendek',
    pic: 'KOMDIGI',
    status: 'done'
  }, {
    horizon: '72j',
    title: 'Engagement dengan influencer terverifikasi',
    pic: 'KOMDIGI',
    status: 'pending'
  }],
  insight: 'Respons inti cepat. Tinggal eksekusi distribusi narasi resmi ke level daerah.'
}, {
  id: 'M-003',
  title: 'Demo buruh tolak upah minimum',
  category: 'SOSIAL',
  severity: 'TINGGI',
  region: 'Jabar, Banten, Jateng',
  responsivenessScore: 55,
  gap: 'Kemnaker belum hadir di pelataran narasi. Dialog tripartit belum diumumkan.',
  stakeholders: [{
    code: 'KEMNAKER',
    name: 'Kementerian Ketenagakerjaan',
    role: 'utama',
    responded: false
  }, {
    code: 'KEMENKO',
    name: 'Kemenko Perekonomian',
    role: 'pendukung',
    responded: true,
    responseTime: '5 jam'
  }, {
    code: 'POLRI',
    name: 'POLRI / Pengamanan',
    role: 'koordinasi',
    responded: true,
    responseTime: '2 jam'
  }, {
    code: 'PEMDA',
    name: 'Pemda Jabar/Banten',
    role: 'utama',
    responded: true,
    responseTime: '4 jam'
  }],
  actions: [{
    horizon: '24j',
    title: 'Statement resmi Menaker',
    pic: 'KEMNAKER',
    status: 'pending'
  }, {
    horizon: '24j',
    title: 'Buka dialog tripartit (pemerintah, pengusaha, buruh)',
    pic: 'KEMNAKER',
    status: 'pending'
  }, {
    horizon: '72j',
    title: 'Review formula UMP regional',
    pic: 'KEMENKO + KEMNAKER',
    status: 'pending'
  }],
  insight: 'Risiko eskalasi tinggi jika Menaker tidak hadir publik dalam 24 jam.'
}, {
  id: 'M-004',
  title: 'Deepfake suara Menteri X',
  category: 'KEAMANAN',
  severity: 'KRITIS',
  region: 'Nasional',
  responsivenessScore: 78,
  gap: 'Pelaporan platform belum ditindaklanjuti TikTok.',
  stakeholders: [{
    code: 'BSSN',
    name: 'BSSN',
    role: 'utama',
    responded: true,
    responseTime: '20 menit'
  }, {
    code: 'KOMDIGI',
    name: 'Komdigi',
    role: 'utama',
    responded: true,
    responseTime: '30 menit'
  }, {
    code: 'POLRI',
    name: 'Cybercrime POLRI',
    role: 'koordinasi',
    responded: true,
    responseTime: '1 jam'
  }, {
    code: 'PLATFORM',
    name: 'TikTok (legal channel)',
    role: 'koordinasi',
    responded: false
  }],
  actions: [{
    horizon: '24j',
    title: 'Takedown via legal request resmi',
    pic: 'KOMDIGI',
    status: 'progress'
  }, {
    horizon: '24j',
    title: 'Klarifikasi video resmi Menteri',
    pic: 'KSP',
    status: 'done'
  }, {
    horizon: '72j',
    title: 'Investigasi sumber & pelaku',
    pic: 'POLRI',
    status: 'progress'
  }],
  insight: 'Respons cepat. Tinggal eksekusi takedown via TikTok Trust & Safety.'
}, {
  id: 'M-005',
  title: 'Kekurangan dokter di Papua',
  category: 'SOSIAL',
  severity: 'SEDANG',
  region: 'Papua, Papua Tengah',
  responsivenessScore: 35,
  gap: 'Belum ada respons resmi dari Kemenkes terkait isu spesifik di 3 kabupaten.',
  stakeholders: [{
    code: 'KEMENKES',
    name: 'Kementerian Kesehatan',
    role: 'utama',
    responded: false
  }, {
    code: 'PEMDA',
    name: 'Pemprov Papua',
    role: 'pendukung',
    responded: true,
    responseTime: '8 jam'
  }, {
    code: 'KEMENDAGRI',
    name: 'Kemendagri',
    role: 'koordinasi',
    responded: false
  }],
  actions: [{
    horizon: '24j',
    title: 'Verifikasi data tenaga medis di 3 kab.',
    pic: 'KEMENKES',
    status: 'pending'
  }, {
    horizon: '72j',
    title: 'Rilis program penempatan dokter spesialis',
    pic: 'KEMENKES',
    status: 'pending'
  }, {
    horizon: '7h',
    title: 'Anggaran tambahan insentif daerah terpencil',
    pic: 'KEMENKES + Kemenkeu',
    status: 'pending'
  }],
  insight: 'Responsiveness rendah. Perlu eskalasi dari KSP/Setkab.'
}];
const sevTone: Record<string, string> = {
  KRITIS: 'bg-red-50 text-red-700 border-red-200',
  TINGGI: 'bg-amber-50 text-amber-700 border-amber-200',
  SEDANG: 'bg-blue-50 text-blue-700 border-blue-200'
};
const roleTone: Record<string, string> = {
  utama: 'bg-primary/10 text-primary border-primary/30',
  pendukung: 'bg-slate-100 text-slate-700 border-slate-200',
  koordinasi: 'bg-purple-50 text-purple-700 border-purple-200'
};
export function MinistryMapping() {
  const [selected, setSelected] = useState<Issue | null>(null);
  const totalStakeholders = issues.reduce((sum, i) => sum + i.stakeholders.length, 0);
  const responded = issues.reduce((sum, i) => sum + i.stakeholders.filter(s => s.responded).length, 0);
  const avgScore = Math.round(issues.reduce((s, i) => s + i.responsivenessScore, 0) / issues.length);
  const criticalCount = issues.filter(i => i.severity === 'KRITIS').length;
  return <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-6" data-id="element-2425">
      <PageHeader group="Komando & Respons" title="Ministry & Agency Mapping" description="Pemetaan stakeholder, kewenangan, status respons, dan rekomendasi tindakan lintas kementerian/lembaga untuk setiap isu." icon={Building2} data-id="element-2426" />

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-id="element-2427">
        <KpiCard tone="red" icon={AlertTriangle} label="Isu Aktif" value={`${issues.length}`} sub={`${criticalCount} status KRITIS`} data-id="element-2428" />
        <KpiCard tone="primary" icon={Network} label="Stakeholder Terlibat" value={`${totalStakeholders}`} sub={`${responded} sudah merespons`} data-id="element-2429" />
        <KpiCard tone="amber" icon={Gauge} label="Avg Responsiveness" value={`${avgScore}/100`} sub={avgScore >= 70 ? 'Optimal' : avgScore >= 50 ? 'Perlu perhatian' : 'Perlu eskalasi'} data-id="element-2430" />
        <KpiCard tone="emerald" icon={ListTodo} label="Aksi 24 Jam" value={`${issues.reduce((s, i) => s + i.actions.filter(a => a.horizon === '24j').length, 0)}`} sub="dalam pipeline" data-id="element-2431" />
      </div>

      {/* Issue list */}
      <Card className="bg-card border-border/60" data-id="element-2432">
        <CardContent className="p-0" data-id="element-2433">
          <div className="px-4 sm:px-5 py-3.5 border-b border-border/60 flex items-center justify-between" data-id="element-2434">
            <div data-id="element-2435">
              <h3 className="font-heading text-sm font-bold" data-id="element-2436">
                Isu & Stakeholder Mapping
              </h3>
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5" data-id="element-2437">
                Klik isu untuk detail kewenangan & rekomendasi
              </p>
            </div>
            <Badge className="bg-primary/10 text-primary border-primary/20 font-mono text-[10px]" data-id="element-2438">
              {issues.length} isu
            </Badge>
          </div>

          <div className="divide-y divide-border/60" data-id="element-2439">
            {issues.map(i => {
            const respondedCount = i.stakeholders.filter(s => s.responded).length;
            return <button key={i.id} onClick={() => setSelected(i)} className="w-full text-left px-4 sm:px-5 py-4 hover:bg-muted/30 transition flex items-center gap-4 group" data-id="element-2440">
                  <div className="flex-1 min-w-0" data-id="element-2441">
                    <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-2442">
                      <span className="font-mono text-[10px] text-muted-foreground" data-id="element-2443">
                        {i.id}
                      </span>
                      <Badge variant="outline" className={`font-mono text-[9px] h-4 ${sevTone[i.severity]}`} data-id="element-2444">
                        {i.severity}
                      </Badge>
                      <span className="font-mono text-[9px] px-1.5 py-0.5 bg-muted rounded text-muted-foreground border border-border/60" data-id="element-2445">
                        {i.category}
                      </span>
                      <span className="font-mono text-[10px] text-muted-foreground" data-id="element-2446">
                        · {i.region}
                      </span>
                    </div>
                    <p className="text-sm font-semibold truncate" data-id="element-2447">{i.title}</p>
                    <div className="flex items-center gap-3 mt-2 flex-wrap" data-id="element-2448">
                      <div className="flex items-center gap-1.5" data-id="element-2449">
                        <Users className="size-3 text-muted-foreground" data-id="element-2450" />
                        <span className="font-mono text-[10px] text-muted-foreground" data-id="element-2451">
                          {respondedCount}/{i.stakeholders.length} stakeholder
                          merespons
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5" data-id="element-2452">
                        <span className="font-mono text-[10px] text-muted-foreground" data-id="element-2453">
                          Score:
                        </span>
                        <div className="w-20 h-1.5 rounded-full bg-muted overflow-hidden" data-id="element-2454">
                          <div className={`h-full ${i.responsivenessScore >= 70 ? 'bg-emerald-500' : i.responsivenessScore >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                        width: `${i.responsivenessScore}%`
                      }} data-id="element-2455" />
                        </div>
                        <span className={`font-mono text-[10px] font-bold ${i.responsivenessScore >= 70 ? 'text-emerald-700' : i.responsivenessScore >= 50 ? 'text-amber-700' : 'text-red-700'}`} data-id="element-2456">
                          {i.responsivenessScore}
                        </span>
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="size-4 text-muted-foreground group-hover:text-foreground group-hover:translate-x-0.5 transition" data-id="element-2457" />
                </button>;
          })}
          </div>
        </CardContent>
      </Card>

      {/* Detail sheet */}
      <Sheet open={!!selected} onOpenChange={o => !o && setSelected(null)} data-id="element-2458">
        <SheetContent className="w-full sm:max-w-xl bg-background border-border/60 p-0 flex flex-col" data-id="element-2459">
          {selected && <>
              <SheetHeader className="px-6 py-5 border-b border-border/60" data-id="element-2460">
                <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-2461">
                  <span className="font-mono text-[10px] text-muted-foreground" data-id="element-2462">
                    {selected.id}
                  </span>
                  <Badge variant="outline" className={`font-mono text-[9px] h-4 ${sevTone[selected.severity]}`} data-id="element-2463">
                    {selected.severity}
                  </Badge>
                  <span className="font-mono text-[9px] px-1.5 py-0.5 bg-muted rounded text-muted-foreground border border-border/60" data-id="element-2464">
                    {selected.category}
                  </span>
                </div>
                <SheetTitle className="text-lg leading-tight" data-id="element-2465">
                  {selected.title}
                </SheetTitle>
                <SheetDescription data-id="element-2466">{selected.region}</SheetDescription>
              </SheetHeader>

              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5" data-id="element-2467">
                {/* Responsiveness */}
                <div className="p-3 rounded-lg border border-border/60 bg-card" data-id="element-2468">
                  <div className="flex items-center justify-between mb-2" data-id="element-2469">
                    <p className="font-mono text-[10px] uppercase text-muted-foreground" data-id="element-2470">
                      Ministry Responsiveness Score
                    </p>
                    <span className={`font-mono text-xs font-bold ${selected.responsivenessScore >= 70 ? 'text-emerald-700' : selected.responsivenessScore >= 50 ? 'text-amber-700' : 'text-red-700'}`} data-id="element-2471">
                      {selected.responsivenessScore}/100
                    </span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-muted overflow-hidden" data-id="element-2472">
                    <div className={`h-full ${selected.responsivenessScore >= 70 ? 'bg-emerald-500' : selected.responsivenessScore >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                  width: `${selected.responsivenessScore}%`
                }} data-id="element-2473" />
                  </div>
                </div>

                {/* Gap analysis */}
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-id="element-2474">
                  <p className="font-mono text-[10px] uppercase text-amber-700 font-bold flex items-center gap-1.5" data-id="element-2475">
                    <AlertTriangle className="size-3" data-id="element-2476" /> Gap Penanganan
                  </p>
                  <p className="text-xs text-amber-900 mt-1.5 leading-relaxed" data-id="element-2477">
                    {selected.gap}
                  </p>
                </div>

                {/* Stakeholders */}
                <div data-id="element-2478">
                  <p className="font-mono text-[10px] uppercase text-muted-foreground mb-2" data-id="element-2479">
                    Stakeholder & Kewenangan ({selected.stakeholders.length})
                  </p>
                  <div className="rounded-lg border border-border/60 bg-card divide-y divide-border/60" data-id="element-2480">
                    {selected.stakeholders.map(s => <div key={s.code} className="flex items-center gap-3 px-3 py-2.5" data-id="element-2481">
                        <div className="size-7 rounded-md bg-muted border border-border/60 flex items-center justify-center shrink-0" data-id="element-2482">
                          <Building2 className="size-3.5 text-muted-foreground" data-id="element-2483" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-2484">
                          <p className="text-xs font-semibold truncate" data-id="element-2485">
                            {s.name}
                          </p>
                          <p className="font-mono text-[10px] text-muted-foreground" data-id="element-2486">
                            {s.code}
                          </p>
                        </div>
                        <Badge variant="outline" className={`font-mono text-[9px] h-4 ${roleTone[s.role]}`} data-id="element-2487">
                          {s.role}
                        </Badge>
                        {s.responded ? <span className="flex items-center gap-1 font-mono text-[10px] text-emerald-700" data-id="element-2488">
                            <CheckCircle2 className="size-3" data-id="element-2489" />
                            {s.responseTime}
                          </span> : <span className="flex items-center gap-1 font-mono text-[10px] text-red-700" data-id="element-2490">
                            <Clock className="size-3" data-id="element-2491" />
                            Belum
                          </span>}
                      </div>)}
                  </div>
                </div>

                {/* Recommendations by horizon */}
                <div data-id="element-2492">
                  <p className="font-mono text-[10px] uppercase text-muted-foreground mb-2" data-id="element-2493">
                    Rekomendasi Tindakan
                  </p>
                  <div className="space-y-2" data-id="element-2494">
                    {['24j', '72j', '7h'].map(h => {
                  const horizonActions = selected.actions.filter(a => a.horizon === h);
                  if (horizonActions.length === 0) return null;
                  const label = h === '24j' ? '24 Jam' : h === '72j' ? '72 Jam' : '7 Hari';
                  return <div key={h} className="rounded-lg border border-border/60 bg-card overflow-hidden" data-id="element-2495">
                          <div className="px-3 py-1.5 bg-muted/40 border-b border-border/60 flex items-center justify-between" data-id="element-2496">
                            <span className="font-mono text-[10px] uppercase tracking-wider text-foreground font-bold" data-id="element-2497">
                              Horizon {label}
                            </span>
                            <span className="font-mono text-[10px] text-muted-foreground" data-id="element-2498">
                              {horizonActions.length} aksi
                            </span>
                          </div>
                          <div className="divide-y divide-border/60" data-id="element-2499">
                            {horizonActions.map((a, idx) => <div key={idx} className="flex items-start gap-2.5 px-3 py-2.5" data-id="element-2500">
                                <div className={`size-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${a.status === 'done' ? 'bg-emerald-100 text-emerald-700' : a.status === 'progress' ? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground'}`} data-id="element-2501">
                                  {a.status === 'done' ? <CheckCircle2 className="size-3" data-id="element-2502" /> : a.status === 'progress' ? <Clock className="size-3" data-id="element-2503" /> : <span className="size-1.5 rounded-full bg-current" data-id="element-2504" />}
                                </div>
                                <div className="flex-1 min-w-0" data-id="element-2505">
                                  <p className="text-xs font-medium leading-snug" data-id="element-2506">
                                    {a.title}
                                  </p>
                                  <p className="font-mono text-[10px] text-muted-foreground mt-0.5" data-id="element-2507">
                                    PIC: {a.pic}
                                  </p>
                                </div>
                              </div>)}
                          </div>
                        </div>;
                })}
                  </div>
                </div>

                {/* AI Insight */}
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-3" data-id="element-2508">
                  <p className="font-mono text-[10px] uppercase text-primary font-bold" data-id="element-2509">
                    AI Analyst Insight
                  </p>
                  <p className="text-xs text-foreground/80 mt-1.5 leading-relaxed" data-id="element-2510">
                    {selected.insight}
                  </p>
                </div>
              </div>

              <div className="border-t border-border/60 p-4 flex gap-2 bg-card/50" data-id="element-2511">
                <Button className="flex-1" onClick={() => {
              toast.success('Tasking dikirim ke stakeholder utama');
              setSelected(null);
            }} data-id="element-2512">
                  <ArrowRight className="size-3.5 mr-1.5" data-id="element-2513" />
                  Kirim Tasking
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => {
              toast.info('Eskalasi ke KSP');
            }} data-id="element-2514">
                  Eskalasi ke KSP
                </Button>
              </div>
            </>}
        </SheetContent>
      </Sheet>
    </div>;
}
function KpiCard({
  tone,
  icon: Icon,
  label,
  value,
  sub
}: {
  tone: 'red' | 'primary' | 'amber' | 'emerald';
  icon: any;
  label: string;
  value: string;
  sub: string;
}) {
  const tones: Record<string, any> = {
    red: {
      bar: 'from-red-400 to-red-600',
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-600'
    },
    primary: {
      bar: 'from-primary/60 to-primary',
      bg: 'bg-primary/10',
      border: 'border-primary/20',
      text: 'text-primary'
    },
    amber: {
      bar: 'from-amber-400 to-amber-600',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-600'
    },
    emerald: {
      bar: 'from-emerald-400 to-emerald-600',
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-600'
    }
  };
  const t = tones[tone];
  return <Card className="bg-card border-border/60 overflow-hidden relative hover:shadow-md transition-all" data-id="element-2515">
      <div className={`absolute top-0 left-0 w-1 h-full bg-gradient-to-b ${t.bar}`} data-id="element-2516" />
      <CardContent className="p-3 sm:p-4 pl-4 sm:pl-5" data-id="element-2517">
        <div className="flex items-start justify-between gap-2" data-id="element-2518">
          <div className="min-w-0" data-id="element-2519">
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground truncate" data-id="element-2520">
              {label}
            </p>
            <p className="font-heading text-2xl sm:text-3xl font-bold mt-1 tracking-tight" data-id="element-2521">
              {value}
            </p>
            <p className="font-mono text-[10px] text-muted-foreground mt-1 truncate" data-id="element-2522">
              {sub}
            </p>
          </div>
          <div className={`size-8 rounded-md ${t.bg} border ${t.border} flex items-center justify-center shrink-0`} data-id="element-2523">
            <Icon className={`size-4 ${t.text}`} data-id="element-2524" />
          </div>
        </div>
      </CardContent>
    </Card>;
}